import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { DialogActions, DialogContent, Grid } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { participantRoles, participantTypes } from 'tods-competition-factory';

import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/addPlayerDialog/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import FilteringNoResults from 'components/emptyStates/FilteringNoResults';
import CollapsableParticipantGrid from 'components/participants/CollapsableParticipantGrid';
import EnhancedTable from 'components/tables/enhancedTable/EnhancedTable';
import PlayersFilters from 'components/tables/players/PlayersFilters';
import { playerColumnIds, useTableColumns } from 'components/tables/players/utils';
import { BodyLarge, H3 } from 'components/typography/Typography';
import { TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { usePromoteToMainDrawDialog } from 'containers/dialogs/PromoteToMainDraw';
import { useSaveTournament } from 'hooks/tournament/useSaveTournament';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { getMainDrawEvents, getTeamsFromParticipants } from 'utils/participant';

const getRowId = (row) => row.participantId;

interface AddPlayerDialogProps {
  open: boolean;
  team: ParticipantInterface;
  tournamentParticipants: ParticipantInterface[];
  onClose: () => void;
}

const { COMPETITOR } = participantRoles;
const { INDIVIDUAL } = participantTypes;
const shownColumnIds = ['selection', 'name', 'city', 'state', 'organisationId'];

const AddPlayerDialog: React.FC<React.PropsWithChildren<AddPlayerDialogProps>> = ({
  open,
  onClose,
  team,
  tournamentParticipants,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const showProfileDialog = usePlayersProfileDialog();
  const openPromoteToMainDrawDialog = usePromoteToMainDrawDialog();
  const { saveTournament } = useSaveTournament();

  const [loading, setLoading] = useState(false);
  const [withoutTeam, setWithoutTeam] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    playerColumnIds.reduce((o, key) => Object.assign(o, { [`${key}`]: shownColumnIds?.includes(key) }), {}),
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const teams = useMemo(() => getTeamsFromParticipants(tournamentParticipants), [tournamentParticipants]);

  const getTeams = useCallback(
    (participantId: string) => {
      return (
        teams?.filter((team) => {
          return team.individualParticipantIds?.includes(participantId);
        }) ?? []
      );
    },
    [teams],
  );

  const participants = useMemo(
    () =>
      tournamentParticipants.filter((p) => {
        const playerTeams = getTeams(p.participantId);
        return (
          p.participantRole === COMPETITOR &&
          p.participantType === INDIVIDUAL &&
          (withoutTeam ? !playerTeams.length : true) &&
          !playerTeams.find((pTeam) => pTeam.participantId === team.participantId)
        );
      }),
    [tournamentParticipants, getTeams, withoutTeam, team.participantId],
  );

  const defaultColumns = useTableColumns({
    classes,
    openPlayerProfile: showProfileDialog,
    t,
  });

  const options: TableOptions<ParticipantInterface> = useMemo(
    () => ({
      columns: defaultColumns,
      data: participants,
      enableRowSelection: true,
      enableMultiRowSelection: true,
      enableColumnFilters: true,
      options: {
        autoResetFilters: false,
      },
      state: {
        columnVisibility,
        rowSelection,
        sorting,
      },
      getRowId: getRowId,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    [columnVisibility, defaultColumns, participants, rowSelection, sorting],
  );

  const tableInstance = useReactTable<ParticipantInterface>(options);
  const filters = tableInstance.getState().columnFilters;
  const columnsVisibility = tableInstance.getState().columnVisibility;
  const globalFilter = tableInstance.getState().globalFilter;
  const { setGlobalFilter, setColumnFilters } = tableInstance;
  const selectedFlatRows = tableInstance.getSelectedRowModel().flatRows;
  const { rows } = tableInstance.getRowModel();

  const selectedTeamMainDraws = useMemo(() => getMainDrawEvents(team)?.flatMap((event) => event.eventId), [team]);

  const handleAddToTeam = useCallback(async () => {
    setLoading(true); // Toggle loading
    const playerIds = selectedFlatRows.map((player) => player.original.participantId);
    const methods = [
      {
        method: 'addIndividualParticipantIds',
        params: {
          groupingParticipantId: team.participantId,
          individualParticipantIds: playerIds,
        },
      },
    ];
    const res = await saveTournament('addToTeam', TOURNAMENT_ENGINE, methods);
    if (res?.data?.mutateTournament?.success) {
      toast.success(t('toasts.playersAddedToTeam', { count: playerIds.length, team: team?.participantName }));
    }

    setLoading(false); // Toggle loading
    onClose();
  }, [selectedFlatRows, team?.participantId, team?.participantName, saveTournament, onClose, t]);

  const handlePromoteDialogOpen = useCallback(() => {
    openPromoteToMainDrawDialog({
      addToTeamParticipants: selectedFlatRows?.map((player) => player?.original) ?? [],
      eventsIds: selectedTeamMainDraws,
      loading: loading,
      promoteParticipants: [team],
      tournamentId: tournamentId,
      onAddToTeam: handleAddToTeam,
    });
  }, [
    handleAddToTeam,
    loading,
    openPromoteToMainDrawDialog,
    selectedFlatRows,
    selectedTeamMainDraws,
    team,
    tournamentId,
  ]);

  const handleInitalAddToTeam = useCallback(() => {
    if (selectedTeamMainDraws?.length > 0) {
      handlePromoteDialogOpen();
    } else {
      handleAddToTeam();
      onClose();
    }
  }, [handleAddToTeam, handlePromoteDialogOpen, onClose, selectedTeamMainDraws]);

  const classesProp = useMemo(() => ({ paper: classes.teamDialogPaper }), [classes.teamDialogPaper]);

  const tableHasResults = !!rows.length;

  return (
    <TMXDialog fullWidth classes={classesProp} maxWidth={'md'} open={open || loading}>
      <H3>{t('teams.addPlayersToTeam')}</H3>
      <DialogContent className={classes.dialogContent}>
        <Grid container>
          <Grid item xs={12}>
            <BodyLarge className={classes.teamText}>
              <span>{t('team')}</span>: {team?.participantName}
            </BodyLarge>
          </Grid>
          <Grid item className={classes.teamText} xs={12}>
            <CollapsableParticipantGrid participants={selectedFlatRows.map((row) => row?.original)} />
          </Grid>
          <Grid item xs={12}>
            <FormGroup className={classes.dialogFilter}>
              <FormControlLabel
                className={classes.playerFilterLabel}
                control={
                  <Checkbox
                    checked={withoutTeam}
                    color={'primary'}
                    onChange={(event) => setWithoutTeam(event.target.checked)}
                  />
                }
                label={t('teams.addPlayerFilter')}
              />
            </FormGroup>
          </Grid>
          <Grid item className={classes.dialogFilter} xs={12}>
            <PlayersFilters
              isSearchAutoFocus
              filters={filters}
              globalFilter={globalFilter}
              setFilters={setColumnFilters}
              setGlobalFilter={setGlobalFilter}
              showFilters={['search', 'events']}
            />
          </Grid>
          <Grid item className={classes.scrollingTableContainer} xs={12}>
            <EnhancedTable
              columnsToggle={false}
              columnsVisibility={columnsVisibility}
              id="add-player-dialog-table"
              rows={rows}
              selectedFlatRows={selectedFlatRows}
              tableInstance={tableInstance}
            />
            {!tableHasResults && <FilteringNoResults />}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <StandardButton onClick={onClose}>{t('actions.cancel')}</StandardButton>
        <StandardButton
          disabled={!selectedFlatRows.length}
          loading={loading}
          variant="primary"
          onClick={handleInitalAddToTeam}
        >
          {t('teams.addPlayersToTeam')}
        </StandardButton>
      </DialogActions>
    </TMXDialog>
  );
};

export default AddPlayerDialog;
